import React from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useReadyForReevaluationModalViewModel } from "./useReadyForReevaluationModalViewModel";
import { ContactIndicatorInitialPatient } from "../useContactIndicatorModalViewModel";
import {
	ModalPaper,
	ModalTop,
	ModalTitleWrap,
	ModalIcon,
	ModalTitle,
	CloseButton,
	ModalDescription,
	ModalContent,
	PatientSearch,
	ContactNotice,
	PatientList,
	SkeletonRow,
	PatientItem,
	PatientSummary,
	ListAvatar,
	ListPhoto,
	PatientInfo,
	ListName,
	ListMeta,
	ContactStatus,
	ScheduleButton,
	ContactPanel,
	ContactTitle,
	ContactAge,
	ContactField,
	InputShell,
	WhatsAppIconWrapper,
	FieldError,
	SendButton,
	LoadMoreButton,
	NoResults,
} from "./styles";
import { ReactComponent as CloseIcon } from "../../../../assets/kinologyIcons/svg/close.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/kinologyIcons/svg/search.svg";
import { ReactComponent as WhatsappMesssageIcon } from "../../../../assets/kinologyIcons/svg/whatsapp2.svg";
import { ReactComponent as FallbackIcon } from "../../../../assets/kinologyIcons/svg/patient.svg";

const useStyles = makeStyles(() =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	})
);

interface ReadyForReevaluationModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialPatient?: ContactIndicatorInitialPatient | null;
	onContactStarted?: (patientId: string) => void;
}

const ReadyForReevaluationModal: React.FC<ReadyForReevaluationModalProps> = ({
	isOpen,
	onClose,
	initialPatient,
	onContactStarted,
}) => {
	const classes = useStyles();
	const modalContentRef = React.useRef<HTMLDivElement | null>(null);
	const patientItemRefs = React.useRef<Record<string, HTMLElement | null>>({});
	const {
		searchQuery,
		activeContactId,
		isLoading,
		isLoadingMore,
		loadMoreCount,
		phoneErrors,
		filteredPatients,
		hasExtraPatients,
		totalPatients,
		error,
		canOpenWhatsApp,
		contactActionState,
		isUpdatingContact,
		contactUpdateError,
		isContactForbidden,
		handleSearchChange,
		handleToggleContact,
		handlePhoneChange,
		handleMessageChange,
		handleSendWhatsApp,
		handleLoadMore,
		retry,
	} = useReadyForReevaluationModalViewModel(
		isOpen,
		initialPatient,
		onContactStarted
	);

	React.useEffect(() => {
		let timerId: any = null;
		if (activeContactId) {
			timerId = setTimeout(() => {
				const container = modalContentRef.current;
				const item = patientItemRefs.current[activeContactId];
				if (container && item) {
					const containerRect = container.getBoundingClientRect();
					const itemRect = item.getBoundingClientRect();
					container.scrollTo({
						top:
							container.scrollTop +
							itemRect.top -
							containerRect.top -
							(container.clientHeight - itemRect.height) / 2,
						behavior: "smooth",
					});
				}
				const activePatient = filteredPatients.find(
					(p) => p.id === activeContactId
				);
				if (activePatient && !activePatient.phone) {
					const inputEl = document.getElementById(`phone-${activeContactId}`);
					if (inputEl) {
						inputEl.focus();
					}
				}
			}, 100);
		}
		return () => {
			if (timerId) {
				clearTimeout(timerId);
			}
		};
	}, [activeContactId, filteredPatients]);

	return (
		<Modal
			aria-labelledby="transition-modal-title"
			aria-describedby="transition-modal-description"
			className={classes.modal}
			open={isOpen}
			onClose={onClose}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 300,
			}}
		>
			<Fade in={isOpen}>
				<ModalPaper>
					<ModalTop>
						<ModalTitleWrap>
							<ModalIcon>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
								>
									<path d="m5 12 4 4L19 6" />
								</svg>
							</ModalIcon>
							<ModalTitle id="transition-modal-title">
								Prontos para Reavaliação
							</ModalTitle>
						</ModalTitleWrap>
						<CloseButton type="button" onClick={onClose} aria-label="Fechar">
							<CloseIcon width={15} height={15} />
						</CloseButton>
					</ModalTop>

					<ModalDescription>
						<p id="transition-modal-description">
							Estes {totalPatients} pacientes fizeram sua avaliação há mais de
							30 dias.
						</p>
					</ModalDescription>

					<ModalContent ref={modalContentRef}>
						<PatientSearch>
							<SearchIcon />
							<input
								type="text"
								placeholder="Buscar paciente..."
								value={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
							/>
						</PatientSearch>
						<ContactNotice>
							O status registra a abertura do WhatsApp, não o envio da mensagem.
						</ContactNotice>

						{error && filteredPatients.length === 0 ? (
							<NoResults>
								Não foi possível carregar os pacientes.{" "}
								<button type="button" onClick={retry}>
									Tentar novamente
								</button>
							</NoResults>
						) : isLoading && !isLoadingMore ? (
							<PatientList aria-label="Carregando pacientes">
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
							</PatientList>
						) : filteredPatients.length > 0 ? (
							<PatientList>
								{filteredPatients.map((p) => {
									const isOpened = activeContactId === p.id;
									let stateType: "default" | "cancel" | "opened" = "default";
									if (isOpened) {
										stateType = "cancel";
									} else if (p.lastContactLabel) {
										stateType = "opened";
									}

									return (
										<PatientItem
											key={p.id}
											ref={(element: HTMLElement | null) => {
												patientItemRefs.current[p.id] = element;
											}}
										>
											<PatientSummary>
												<ListAvatar hasPhoto={!!p.photoUrl}>
													{p.photoUrl ? (
														<ListPhoto src={p.photoUrl} alt={p.name} />
													) : (
														<FallbackIcon
															width="20px"
															height="20px"
															fill="#ffffff"
														/>
													)}
												</ListAvatar>
												<PatientInfo>
													<ListName>{p.name}</ListName>
													<ListMeta>
														Última avaliação:{" "}
														<span style={{ fontFamily: "NotoSans-Bold" }}>
															{p.date}
														</span>
													</ListMeta>
													{p.lastContactLabel && (
														<ContactStatus>
															<span>
																{p.lastContactLabel} {p.lastContactAuthorLabel}
															</span>
														</ContactStatus>
													)}
												</PatientInfo>
												<ScheduleButton
													type="button"
													stateType={stateType}
													disabled={isContactForbidden(p.id)}
													onClick={() => handleToggleContact(p.id)}
												>
													{isOpened
														? "Cancelar"
														: p.lastContactLabel
														? "Enviar novamente"
														: "Convidar para reavaliação"}
												</ScheduleButton>
											</PatientSummary>

											{isOpened && (
												<ContactPanel>
													<ContactTitle>{p.name}</ContactTitle>
													<ContactAge>
														{p.days} dias desde a última avaliação
													</ContactAge>

													<ContactField>
														<label htmlFor={`phone-${p.id}`}>
															Celular com DDD
														</label>
														<InputShell>
															<WhatsAppIconWrapper>
																<WhatsappMesssageIcon />
															</WhatsAppIconWrapper>
															<input
																id={`phone-${p.id}`}
																type="tel"
																value={p.phone}
																onChange={(e) =>
																	handlePhoneChange(p.id, e.target.value)
																}
																placeholder="(00) 00000-0000"
															/>
														</InputShell>
														{(phoneErrors[p.id] || !canOpenWhatsApp(p.id)) && (
															<FieldError role="alert">
																Informe um celular válido com DDD.
															</FieldError>
														)}
														{contactUpdateError(p.id) && (
															<FieldError>
																{contactUpdateError(p.id)}
															</FieldError>
														)}
													</ContactField>

													<ContactField>
														<label htmlFor={`message-${p.id}`}>Mensagem</label>
														<textarea
															id={`message-${p.id}`}
															value={p.message || ""}
															onChange={(e) =>
																handleMessageChange(p.id, e.target.value)
															}
														/>
													</ContactField>

													<SendButton
														type="button"
														disabled={
															!canOpenWhatsApp(p.id) ||
															isUpdatingContact(p.id) ||
															isContactForbidden(p.id)
														}
														aria-busy={isUpdatingContact(p.id)}
														onClick={() => handleSendWhatsApp(p.id)}
													>
														{contactActionState(p.id) === "registering"
															? "Registrando contato..."
															: contactActionState(p.id) === "opening"
															? "Abrindo WhatsApp..."
															: isUpdatingContact(p.id)
															? "Atualizando telefone..."
															: "Abrir conversa no WhatsApp"}
													</SendButton>
												</ContactPanel>
											)}
										</PatientItem>
									);
								})}
							</PatientList>
						) : (
							<NoResults>Nenhum paciente encontrado.</NoResults>
						)}

						{hasExtraPatients && (
							<LoadMoreButton
								type="button"
								onClick={handleLoadMore}
								disabled={isLoadingMore}
							>
								{isLoadingMore
									? "Carregando..."
									: `Carregar mais ${loadMoreCount} pacientes`}
							</LoadMoreButton>
						)}
					</ModalContent>
				</ModalPaper>
			</Fade>
		</Modal>
	);
};

export default ReadyForReevaluationModal;
